import React from 'react';

import { RequiredTag } from '@components/Forms/RequiredTag';
import { type ClassMap, makeMuiStyles, useCommonStyles } from '@styles';
import clsx from 'clsx';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    padding: '1rem 0',
  },
  label: {
    ...common.middleText,
    ...common.fontWeight500,
    width: 180,
    flexShrink: 0,
    color: theme.palette.grey[800],
    '$light &': {
      color: theme.palette.grey[700],
      fontWeight: 'initial',
    },
    marginRight: '0.5rem',
  },
  labelDestructive: {
    ...common.fontWeight700,
  },
  value: {
    ...common.middleText,
    color: theme.palette.grey[700],
    whiteSpace: 'pre-wrap',
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  light: {},
  default: {},
  vertical: {
    ...common.flexColumn,
    padding: '0.6875rem',
    paddingLeft: 0,
  },
  obsolete: {
    textDecoration: 'line-through',
  },
}));

export interface ManagementFieldDisplayProps {
  label: React.ReactNode;
  value?: React.ReactNode;
  required?: boolean;
  labelDestructive?: boolean;
  variant?: 'default' | 'light' | 'vertical';
  classes?: ClassMap<'value' | 'container'>;
  obsolete?: boolean;
}

export const ManagementFieldDisplay: React.FC<ManagementFieldDisplayProps> = (props) => {
  const {
    label,
    labelDestructive = false,
    value,
    variant = 'default',
    required,
    classes: customClasses = {},
    obsolete,
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={clsx(classes.container, classes[variant], customClasses.container)}>
      <div className={clsx(classes.flexInline, classes.label)}>
        <span className={clsx(labelDestructive && classes.labelDestructive)}>{label}</span>
        {required && (
          <div style={{ paddingLeft: '0.5rem' }}>
            <RequiredTag />
          </div>
        )}
      </div>
      <div
        className={clsx(
          classes.flexInline,
          classes.value,
          customClasses.value,
          obsolete && classes.obsolete,
        )}
      >
        {value}
      </div>
    </div>
  );
};
